import React, { useEffect, useMemo, useState } from 'react';
import { domain } from './Domain';
import useApiRequest from './useApiRequest';
import '../css/CategoryComponent.css';
import { DNA } from 'react-loader-spinner';

const LookupManager = ({
    title,
    endpoint,
    placeholder,
    loadingLabel,
    emptyLabel,
    includeHeader = false,
}) => {
    const { postRequest, getRequest, putRequest, deleteRequest } = useApiRequest();
    const [name, setName] = useState('');
    const [header, setHeader] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const itemCount = items.length;
    const canSubmit = name.trim().length > 0 && !saving;

    const actionLabel = editingId ? 'Update' : 'Add';
    const submitLabel = saving
        ? editingId
            ? 'Saving...'
            : 'Adding...'
        : `${actionLabel} ${title}`;

    const fetchItems = async () => {
        setLoading(true);
        const response = await getRequest(`${domain}/${endpoint}`);
        setItems(Array.isArray(response) ? response : []);
        setLoading(false);
    };

    useEffect(() => {
        fetchItems();
    }, []);

    const handleAddOrUpdate = (item) => {
        if (!item?.id) return;
        setItems((prev) => {
            const index = prev.findIndex((i) => i.id === item.id);
            if (index === -1) return [item, ...prev];
            const copy = [...prev];
            copy[index] = item;
            return copy;
        });
    };

    const resetForm = () => {
        setName('');
        setHeader(false);
        setEditingId(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        setSaving(true);
        try {
            const fd = new FormData();
            fd.append('name', name.trim());
            if (includeHeader) fd.append('header', header);

            const response = editingId
                ? await putRequest(`${domain}/${endpoint}/${editingId}`, fd)
                : await postRequest(`${domain}/${endpoint}`, fd);

            handleAddOrUpdate(response);
            resetForm();
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this item?')) return;
        const response = await deleteRequest(`${domain}/${endpoint}/${id}`);
        if (response) {
            setItems((prev) => prev.filter((item) => item.id !== id));
        }
    };

    const handleEdit = (item) => {
        setName(item.name || '');
        setEditingId(item.id);
        if (includeHeader) setHeader(Boolean(item.header));
    };

    const sortedItems = useMemo(
        () =>
            [...items].sort((a, b) =>
                String(a.name || '').localeCompare(String(b.name || ''))
            ),
        [items]
    );

    return (
        <section className="lookup-manager">
            <header className="lookup-manager-header">
                <h3 className="lookup-manager-title">{title}</h3>
                <p className="lookup-manager-meta mb-0">
                    {itemCount} item{itemCount !== 1 ? 's' : ''}
                </p>
            </header>

            <form className="lookup-form" onSubmit={handleSubmit}>
                <div className="lookup-form-main">
                    <label className="lookup-label" htmlFor={`lookup-${endpoint}`}>
                        {title} name
                    </label>
                    <input
                        id={`lookup-${endpoint}`}
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={placeholder}
                        className="lookup-input"
                    />
                </div>
                {includeHeader && (
                    <label className="lookup-checkbox">
                        <input
                            type="checkbox"
                            checked={header}
                            onChange={(e) => setHeader(e.target.checked)}
                        />
                        <span>Visible on navigation</span>
                    </label>
                )}
                <div className="lookup-actions">
                    <button
                        type="submit"
                        className="btn btn-primary btn-sm"
                        disabled={!canSubmit}
                    >
                        {submitLabel}
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        disabled={!editingId}
                        onClick={resetForm}
                    >
                        Cancel edit
                    </button>
                </div>
            </form>

            <div className="lookup-list">
                {loading ? (
                    <p className="lookup-loading">
                        <DNA
                            visible
                            height="56"
                            width="56"
                            ariaLabel="loading"
                            wrapperClass="lookup-dna"
                        />
                        <span>{loadingLabel}</span>
                    </p>
                ) : sortedItems.length === 0 ? (
                    <p className="lookup-empty mb-0">{emptyLabel}</p>
                ) : (
                    <ul className="lookup-items list-unstyled mb-0">
                        {sortedItems.map((item) => (
                            <li key={item.id} className="lookup-item">
                                <div className="lookup-item-name">
                                    {item.name}
                                    {includeHeader && item.header && (
                                        <span className="lookup-pill">Header</span>
                                    )}
                                </div>
                                <div className="lookup-item-actions">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => handleDelete(item.id)}
                                    >
                                        Delete
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => handleEdit(item)}
                                    >
                                        Edit
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </section>
    );
};

export default LookupManager;
